import { Collapse } from "@blueprintjs/core";
import React from "react";

import autobind from "autobind-decorator";
import { Fragment, FragmentProvider, FragmentRenderer, IDataFragment } from "core/components/FragmentProvider";
import { IViewProps, View } from "core/decorators/View";
import ILocalCacheService from "core/service/cache/ILocalCacheService";
import ServiceRegistry from "core/util/ServiceRegistry";
import StringUtil from "core/util/StringUtil";
import StyledTable from "./StyledTable";
import TableViewConfig from "./TableViewConfig";
import { IconNames } from "@blueprintjs/icons";
import Icon from "../icon/Icon";
import { ButtonView } from "../button/Button.view";
import { ColumnHeaderView } from "../columnHeader/ColumnHeader.view";

/**
 * basic table view
 */

type ResolveIdFunction<D> = (dataItem: D, rowIndex: number) => string;

export interface ICellProps {
	rowIndex: number;
	colIndex: number;
	primary?: boolean;
}

export interface ITableColumn<T> extends Fragment<T & ICellProps> {
	primary?: boolean;
	header: IDataFragment<T>['label'];
	renderSkeleton?: FragmentRenderer<{rowIndex?: number, colIndex?: number, primary?: boolean}>; // override for the field in fragment, provided more info
	sort? : string;
	sortable?: boolean;
}

interface ITableViewProps<D, T> extends IViewProps<TableViewConfig> {
	data: D[];
	columns: Array<ITableColumn<T>>;
	getColumnProps?: (data: D) => T; // used for passing things other than the elements of the data list to the column fragments
	getId: ResolveIdFunction<D>;
	isExpanded?: (props: T & {rowIndex: number}) => boolean;
	renderExpand?: (props: T & {rowIndex: number}) => JSX.Element;
	empty?: JSX.Element;
	skeleton?: boolean;
	setSort?: (sort: string) => void;
}

interface ITableViewState {
	sortOrder: boolean;
	currentSortedColumn: string;
}
@View({id: "table", configClass: TableViewConfig})
export class TableView<D, T> extends React.Component<ITableViewProps<D, T>,ITableViewState> {
	private static STORAGE_KEY = "view:table:rows:";

	@autobind
	private getStorageKey() {
		const { panelInstance, viewKey } = this.props;
		return StringUtil.format("view:table:rows:%s:%s", panelInstance, viewKey);
	}

	public state = { sortOrder: true, currentSortedColumn: ""};

	public render() {
		const { config, columns = [], data, empty, skeleton = true, panelInstance } = this.props;
		let savedRowCount = 0;

		// TODO move this to proper lifecycle functions
		if(skeleton) {
			const storageService = ServiceRegistry.get(ILocalCacheService);
			const key = this.getStorageKey();

			// check if we are reading or saving the row count
			if(Array.isArray(data)) {

				// we have some data, update the row count guess
				storageService.putNumber(key, data.length);

			} else {

				// try to read saved row count
				savedRowCount = storageService.getNumber(key, 0);
			}
		}

		return (
			<StyledTable config={config}>
				<thead>
					<tr>
						{columns.map(this.renderHeader)}
					</tr>
				</thead>
				<tbody>
					{Array.isArray(data) ? (
						data.map(this.renderRow)
					) : (
						(skeleton && this.renderSkeleton(savedRowCount || config.defaultSkeletonCount)) || empty || null
					)}
				</tbody>
			</StyledTable>
		);
	}

	private renderHeader = (column: ITableColumn<T>, rowIndex: number) => <th key={column.name}>
																			{column.sortable ?
																				<React.Fragment>
																				<a onClick={this.setSort(column.sort)}>
																					<ColumnHeaderView viewKey='column.sortable.header'>
																						{FragmentProvider.createElement(this.props.viewKey, column.header, {rowIndex})}
																					</ColumnHeaderView>
																				</a>
																				{this.state.currentSortedColumn === column.sort ? (this.state.sortOrder) ? <Icon icon={IconNames.CARET_DOWN}/> 
																												  					: <Icon icon={IconNames.CARET_UP}/>
																																	: ''}
																																	</React.Fragment>
																				: <ColumnHeaderView viewKey='column.header' style={{cursor: "default"}}>
																					{FragmentProvider.createElement(this.props.viewKey, column.header, {rowIndex})} 
																				</ColumnHeaderView> 
																			}
																		</th>;

	private renderRow = (dataItem: D, rowIndex: number, array?: D[], skeleton?: boolean, getId?: ResolveIdFunction<D>) => {
		const { columns = [], isExpanded, renderExpand, viewKey, getColumnProps } = this.props;

		const fragmentProps = ((getColumnProps && getColumnProps(dataItem)) || dataItem) as T;

		const key = (getId && getId(dataItem, rowIndex)) || this.props.getId(dataItem, rowIndex);

		const ColumnFragmentProvider = FragmentProvider.forFragments(viewKey, columns.reduce((acc, { name, ...obj }) => {
			acc[name] = obj;
			return acc;
		}, {}));

		return [
			(
			<tr key={key}>
				{columns.map((col, colIndex) => {
					const skeletonProps = {rowIndex, colIndex, primary: col.primary};

					const cell = skeleton ? (
						(col.renderSkeleton && FragmentProvider.createElement(viewKey, col.renderSkeleton, skeletonProps)) || this.renderSkeletonCell(rowIndex, colIndex, col.primary)
					) : (
						<ColumnFragmentProvider fragments={[col.name]} fragmentProps={{...fragmentProps, ...skeletonProps}} />
					);

					return (
						<td key={col.name} className={col.primary ? "primary" : "secondary"}>
							{cell}
						</td>
					);
				})}
			</tr>
			),
			(renderExpand && isExpanded) && (
				<tr key={key + '-expand'}>
					<td colSpan={columns.length}>
						<Collapse isOpen={isExpanded({...fragmentProps, rowIndex})}>
							{renderExpand({...fragmentProps, rowIndex})}
						</Collapse>
					</td>
				</tr>
			)
		];
	}

	@autobind
	private renderSkeletonCell(rowIndex: number, colIndex: number, primary: boolean) {
		const { skeletonCellHeightPrimary, skeletonCellHeightSecondary } = this.props.config;

		return (
			<div
				className="bp3-skeleton"
				style={{minHeight: primary ? skeletonCellHeightPrimary : skeletonCellHeightSecondary + "px"}}
			/>
		);
	}

	private renderSkeleton(rows: number) {
		return Array.from({length: rows}, (v, i) =>
			this.renderRow(null, i, null, true, () => i.toString())
		);
	}
	private setSort = (sort: string) => () => {
		const { setSort } = this.props;
		this.setState({sortOrder: (this.state.sortOrder) ? false : true, currentSortedColumn: sort});
		setSort((!this.state.sortOrder ? '-' : '') + sort);		
	}
}



// WEBPACK FOOTER //
// ./src/common/views/table/Table.view.tsx